
import { useState, useEffect, useCallback } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { SearchOptions, SearchResult } from './types';

interface KnowledgeSearchProps {
  onSearch: (query: string, options?: SearchOptions) => Promise<SearchResult[]>;
  isSearching: boolean;
}

export function KnowledgeSearch({ onSearch, isSearching }: KnowledgeSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce time

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle debounced search
  useEffect(() => {
    if (debouncedQuery !== undefined) {
      onSearch(debouncedQuery).catch(error => 
        console.error("Error during debounced search:", error)
      );
    }
  }, [debouncedQuery, onSearch]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
  }, []);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch(searchQuery).catch(error => console.error("Error during search:", error));
    }
  }, [onSearch, searchQuery]);

  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Input
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={handleSearchInputChange}
          onKeyPress={handleKeyPress}
          className="pr-10"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      <Button onClick={() => onSearch(searchQuery)} disabled={isSearching}>
        {isSearching ? 'Searching...' : 'Search'}
      </Button>
    </div>
  );
}
